import * as React from 'react'
import type { ColumnDef } from '@tanstack/react-table'

import { DataTableInfinite } from '@/components/data-table/data-table-infinite'
import type { DataTableFilterField } from '@/components/data-table/types'
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { TextWithTooltip } from '@/components/custom/text-with-tooltip'
import { DataTableStoreProvider } from '@/lib/store/provider/DataTableStoreProvider'
import { useNuqsAdapter } from '@/lib/store/adapters/nuqs'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { useFilterActions } from '@/lib/store/hooks/useFilterActions'
import { createSchema, field } from '@/lib/store/schema'
import { columnFiltersToFilterConditions } from '@/features/calls/utils/mergeFilters'
import { TabRefreshButton } from '@/components/panel/TabRefreshButton'
import { useProvidersQuery } from '@/hooks/useProvidersQuery'
import type { ProviderRecord } from '@/features/providers/types'
import { ImportDataButton } from '@/features/data-import'
import { ProvidersSheetWrapper } from '@/features/providers/components/ProvidersSheetDetails'

const TABLE_ID = 'providers'

// Filter rail: three contains-filters (input). Schema keys MUST match the
// filterField `value`s and the query-param names relay's /data/providers accepts.
const providersFilterFields: DataTableFilterField<ProviderRecord>[] = [
  {
    label: 'Provider Name',
    value: 'provider_name',
    type: 'input',
    defaultOpen: true,
  },
  { label: 'Specialty', value: 'specialty', type: 'input', defaultOpen: true },
  {
    label: 'Credentials',
    value: 'credentials',
    type: 'input',
    defaultOpen: true,
  },
]

const providersSchema = createSchema({
  provider_name: field.string().default(null),
  specialty: field.string().default(null),
  credentials: field.string().default(null),
})

const UnavailableData = () => <span className="text-muted-foreground">-</span>

const columns: ColumnDef<ProviderRecord>[] = [
  {
    accessorKey: 'provider_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Provider Name" />
    ),
    cell: ({ row }) => {
      const value = row.original.provider_name
      if (!value) return <UnavailableData />
      return <TextWithTooltip text={value} className="max-w-[220px]" />
    },
  },
  {
    accessorKey: 'specialty',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Specialty" />
    ),
    cell: ({ row }) => {
      const value = row.original.specialty
      if (!value) return <UnavailableData />
      return <TextWithTooltip text={value} className="max-w-[220px]" />
    },
  },
  {
    accessorKey: 'credentials',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Credentials" />
    ),
    cell: ({ row }) => {
      const value = row.original.credentials
      if (!value) return <UnavailableData />
      return <TextWithTooltip text={value} className="max-w-[220px]" />
    },
  },
]

function ProvidersInner() {
  const storeState = useFilterState()
  const { resetAllFilters } = useFilterActions()
  // Only real filter fields become query params — exclude store metadata
  // (sort, selected-row uuid) so selecting a row doesn't churn the query key.
  const apiFilters = React.useMemo(() => {
    const ids = new Set(providersFilterFields.map((f) => String(f.value)))
    const columnFilters = Object.entries(storeState ?? {})
      .filter(([key, value]) => {
        if (!ids.has(key)) return false
        if (value === null || value === undefined) return false
        if (Array.isArray(value)) return value.length > 0
        return String(value).trim().length > 0
      })
      .map(([id, value]) => ({ id, value }))
    return columnFiltersToFilterConditions(columnFilters)
  }, [storeState])

  const query = useProvidersQuery({
    limit: 50,
    sort_by: 'provider_name',
    sort_order: 'asc',
    filters: apiFilters.length > 0 ? apiFilters : undefined,
  })

  const data = React.useMemo(
    () => query.data?.pages.flatMap((p) => p.data) ?? [],
    [query.data]
  )
  const totalRows = query.data?.pages[0]?.pagination.total ?? undefined

  const getRowId = React.useCallback((row: ProviderRecord) => row.id, [])

  const SheetSlot = React.useMemo(() => <ProvidersSheetWrapper />, [])

  const ToolbarActions = React.useMemo(
    () => (
      <>
        <ImportDataButton entity="provider" entityLabel="Provider" />
        <TabRefreshButton
          onRefresh={query.refetch}
          isFetching={query.isFetching}
        />
      </>
    ),
    [query.refetch, query.isFetching]
  )

  return (
    <DataTableInfinite<ProviderRecord, unknown>
      tableId={TABLE_ID}
      columns={columns}
      data={data}
      filterFields={providersFilterFields}
      manualFiltering
      isRefetching={query.isPlaceholderData}
      isError={query.isError}
      error={query.error}
      entityLabel="providers"
      onClearFilters={resetAllFilters}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      hasNextPage={query.hasNextPage}
      fetchNextPage={query.fetchNextPage}
      refetch={query.refetch}
      totalRows={totalRows}
      totalRowsFetched={data.length}
      dataUpdatedAt={query.dataUpdatedAt}
      getRowId={getRowId}
      sheetSlot={SheetSlot}
      toolbarActions={ToolbarActions}
      autoColumnWidth
    />
  )
}

export function Providers() {
  const adapter = useNuqsAdapter(providersSchema.definition, {
    id: TABLE_ID,
    history: 'replace',
    // Namespace URL params (`${id}.`) so tables sharing the sidebar's carried
    // location.search don't bleed each other's same-named filters across tabs.
    namespaceUrlKeys: true,
  })

  return (
    <DataTableStoreProvider adapter={adapter}>
      <ProvidersInner />
    </DataTableStoreProvider>
  )
}
